import { useId } from 'react';
import type { Comparable } from '../types';
import { formatMan } from '../lib/format';

interface Props {
  /** 予測幅の下限・上限 (円). */
  low: number;
  high: number;
  /** 点推定 (円). */
  estimate: number;
  /** 近隣の取引事例。照会面積に換算して同じ軸に打つ。 */
  comparables: Comparable[];
  /** 換算の基準面積 (㎡)。null なら事例を重ねない。 */
  refArea: number | null;
  /** 基準面積の出どころ。ラベルの文言を変える。 */
  refAreaSource: 'input' | 'median';
}

interface Plotted {
  price: number;
  label: string;
}

const W = 320;
const H = 84;
const PAD = 8;

/**
 * 価格帯スケール — the one chart in the app.
 *
 * The model's output is an interval, not a point, so the interval is what
 * gets drawn: a band from low to high with the point estimate as a tick
 * inside it. Every nearby transaction is normalised to the queried area and
 * plotted on that same axis, which is the only way to see whether the
 * estimate sits among the real trades or off to one side of them. The axis
 * domain stretches to include the comparables rather than clipping them, so
 * an estimate that falls outside the observed range stays visibly outside.
 */
export default function PriceScale({
  low,
  high,
  estimate,
  comparables,
  refArea,
  refAreaSource,
}: Props) {
  const gradId = useId();

  const plotted: Plotted[] =
    refArea && refArea > 0
      ? comparables
          .filter((c) => c.area > 0 && c.tradePrice > 0)
          .map((c) => ({
            price: (c.tradePrice / c.area) * refArea,
            label: `${c.districtName} ${c.floorPlan} ${c.area.toFixed(1)}㎡ 築${c.buildingYear}`,
          }))
      : [];

  const values = [low, high, estimate, ...plotted.map((p) => p.price)].filter(
    (v) => isFinite(v) && v > 0,
  );
  if (values.length === 0) return null;

  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  // Pad the domain so an endpoint never lands exactly on the axis terminus.
  const pad = Math.max(1, (rawMax - rawMin) * 0.08);
  const dMin = rawMin - pad;
  const dMax = rawMax + pad;
  const span = Math.max(1, dMax - dMin);

  const x = (v: number) => PAD + ((v - dMin) / span) * (W - 2 * PAD);

  const axisY = 32;
  const bandH = 20;
  const xLow = x(low);
  const xHigh = x(high);
  const xEst = x(estimate);

  const summary = `推定 ${formatMan(estimate)}万円、予測幅 ${formatMan(low)}万円から${formatMan(high)}万円${
    plotted.length > 0 ? `、近隣の取引事例 ${plotted.length} 件を同じ軸に表示` : ''
  }`;

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label={summary}
      >
        <defs>
          {/* The band fades at both ends: the interval is a confidence
              region, not a hard-edged box, and drawing it with crisp walls
              would claim more precision than the model has. */}
          <linearGradient id={gradId} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="var(--mk-primary-wash)" stopOpacity="0.35" />
            <stop offset="30%" stopColor="var(--mk-primary-wash)" stopOpacity="1" />
            <stop offset="70%" stopColor="var(--mk-primary-wash)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--mk-primary-wash)" stopOpacity="0.35" />
          </linearGradient>
        </defs>

        <rect
          x={xLow}
          y={axisY - bandH / 2}
          width={Math.max(1, xHigh - xLow)}
          height={bandH}
          fill={`url(#${gradId})`}
        />

        {/* Interval terminals — short uprights, like the end marks on a
            measuring scale. */}
        <line x1={xLow} y1={axisY - bandH / 2} x2={xLow} y2={axisY + bandH / 2}
              stroke="var(--mk-primary)" strokeWidth={1} />
        <line x1={xHigh} y1={axisY - bandH / 2} x2={xHigh} y2={axisY + bandH / 2}
              stroke="var(--mk-primary)" strokeWidth={1} />

        <line x1={PAD} y1={axisY} x2={W - PAD} y2={axisY}
              stroke="var(--mk-rule-strong)" strokeWidth={1} />

        {/* The point estimate: the one accent-coloured mark, matching the
            price pin on the map and the figure above it. */}
        <line x1={xEst} y1={axisY - 19} x2={xEst} y2={axisY + 19}
              stroke="var(--mk-accent)" strokeWidth={4} strokeLinecap="round" />

        {plotted.length > 0 && (
          <g>
            {plotted.map((p, i) => (
              <circle
                key={i}
                cx={x(p.price)}
                cy={axisY + 33}
                r={4}
                fill="var(--mk-sheet)"
                stroke="var(--mk-primary)"
                strokeWidth={1.5}
              >
                <title>{`${p.label} → 換算 ${formatMan(p.price)}万円`}</title>
              </circle>
            ))}
          </g>
        )}
      </svg>

      {/* The axis reads left to right in text as well as in the drawing, so
          the endpoints are legible to a screen reader and to anyone who
          skips the graphic. */}
      <div className="mt-2 flex items-baseline justify-between text-ink-3">
        <span className="text-[11px]">
          <span className="fig text-[17px] text-primary-text">{formatMan(low)}</span> 万円
        </span>
        <span className="label text-accent">予測幅</span>
        <span className="text-[11px]">
          <span className="fig text-[17px] text-primary-text">{formatMan(high)}</span> 万円
        </span>
      </div>

      {plotted.length > 0 && (
        <figcaption className="mt-3 text-[11px] leading-jp text-ink-3">
          下段の ○ は近隣の取引 {plotted.length} 件。
          {refAreaSource === 'input'
            ? `入力した ${refArea!.toFixed(1)}㎡ に換算`
            : `近隣事例の中央値 ${refArea!.toFixed(1)}㎡ に換算`}
          して同じ軸に並べています。
        </figcaption>
      )}
    </figure>
  );
}
